// The phone apps' web-view widget (VisionPlatform specs/mobile-dashboards-2 MD2-03): the app opens
// /embed/widget/{dashboard_id}/{widget_id} in its own web view and signs it in with its own token, so the person never
// signs in a second time. The host hands the session over before the page's scripts run:
//   iOS      a WKUserScript at document start sets window.creavisionEmbed = { token, org, lang }, and answers
//            window.webkit.messageHandlers.creavisionEmbed.postMessage("token") with window.creavisionEmbedSetToken(t)
//   Android  a JavaScript interface CreavisionEmbedHost whose session() returns the same object as JSON, current at
//            every call
// The page never reads or stores the token anywhere else, and only the embed route reads this session.

export type EmbedSession = { token: string; org: string; lang?: string };

type Listener = (token: string) => void;

declare global {
  interface Window {
    creavisionEmbed?: unknown;
    creavisionEmbedSetToken?: (token: string) => void;
    CreavisionEmbedHost?: { session(): string };
    webkit?: { messageHandlers?: { creavisionEmbed?: { postMessage(message: string): void } } };
  }
}

const TOKEN_WAIT_MS = 5_000;

let session: EmbedSession | undefined;
const listeners = new Set<Listener>();

function parse(raw: unknown): EmbedSession | undefined {
  const v = typeof raw === "string" ? safeJson(raw) : raw;
  if (!v || typeof v !== "object") return undefined;
  const { token, org, lang } = v as Record<string, unknown>;
  if (typeof token !== "string" || !token || typeof org !== "string" || !org) return undefined;
  return { token, org, lang: typeof lang === "string" ? lang : undefined };
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

function fromHost(): EmbedSession | undefined {
  if (typeof window === "undefined") return undefined;
  if (window.CreavisionEmbedHost) {
    try {
      return parse(window.CreavisionEmbedHost.session());
    } catch {
      return undefined;
    }
  }
  return parse(window.creavisionEmbed);
}

function setToken(token: string): void {
  if (!session || !token || token === session.token) return;
  session = { ...session, token };
  for (const l of listeners) l(token);
}

/**
 * Starts the embed session from what the phone app handed over; undefined in a plain browser, where the embed page
 * shows only "Open it in the Creavision app". Called once by the embed route, before anything is read.
 */
export function startEmbedSession(): EmbedSession | undefined {
  session = fromHost();
  if (session && typeof window !== "undefined") window.creavisionEmbedSetToken = setToken;
  return session;
}

/** The embed session, when the page runs inside a phone app's web-view widget. */
export function embedSession(): EmbedSession | undefined {
  return session;
}

/** The token the phone app gave last; the API client and the live socket use it in place of the browser's. */
export function embedToken(): string | undefined {
  return session?.token;
}

/**
 * Asks the phone app for a fresh token after the server refused one: Android answers at once, iOS through
 * creavisionEmbedSetToken. Undefined when no new token came within a few seconds.
 */
export function renewEmbedToken(): Promise<string | undefined> {
  if (!session) return Promise.resolve(undefined);
  const before = session.token;
  if (window.CreavisionEmbedHost) {
    const next = fromHost();
    if (next && next.token !== before) setToken(next.token);
    return Promise.resolve(session.token !== before ? session.token : undefined);
  }
  const handler = window.webkit?.messageHandlers?.creavisionEmbed;
  if (!handler) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const done = (t: string | undefined) => {
      clearTimeout(timer);
      listeners.delete(onToken);
      resolve(t);
    };
    const onToken: Listener = (t) => done(t);
    const timer = setTimeout(() => done(undefined), TOKEN_WAIT_MS);
    listeners.add(onToken);
    handler.postMessage("token");
  });
}

/** Called with every new token the phone app gives; returns the unsubscribe. */
export function onEmbedToken(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function resetEmbedForTests(): void {
  session = undefined;
  listeners.clear();
  if (typeof window !== "undefined") {
    delete window.creavisionEmbed;
    delete window.creavisionEmbedSetToken;
    delete window.CreavisionEmbedHost;
    delete window.webkit;
  }
}
